import { TASK_STATUSES } from '@gozoki/api-types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createTask,
  deleteTask,
  updateTask,
  updateTaskPosition,
} from '../mutations/tasks.mutations'
import { fetchTasks, type TaskResponse } from '../queries/tasks.queries'

export const useTasks = () =>
  useQuery({
    queryKey: ['real-estate-tasks'],
    queryFn: fetchTasks,
  })

export const useCreateTask = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createTask,
    onSuccess: (newTask) => {
      // Add the new task to the cache
      queryClient.setQueryData<TaskResponse[]>(['real-estate-tasks'], (old) => {
        if (!old) return [newTask]
        return [...old, newTask]
      })

      // Refetch in background to ensure we don't miss any server-side computed fields
      queryClient.invalidateQueries({ queryKey: ['real-estate-tasks'] })
    },
  })
}

export const useUpdateTask = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateTask,
    onSuccess: (updatedTask) => {
      // Update the task in the cache with fresh data from server
      queryClient.setQueryData<TaskResponse[]>(['real-estate-tasks'], (old) => {
        if (!old) return old
        return old.map((task) => (task.id === updatedTask.id ? updatedTask : task))
      })

      // Refetch in background to guarantee consistency
      queryClient.invalidateQueries({ queryKey: ['real-estate-tasks'] })
    },
  })
}

export const useDeleteTask = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['real-estate-tasks'] })
    },
  })
}

export const useUpdateTaskPosition = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateTaskPosition,
    // Optimistic update: immediately reorder the cache before the API call completes
    onMutate: async ({ id, status, position }) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['real-estate-tasks'] })

      // Snapshot the previous value for rollback
      const previousTasks = queryClient.getQueryData<TaskResponse[]>(['real-estate-tasks'])

      // Optimistically update the cache immediately
      queryClient.setQueryData<TaskResponse[]>(['real-estate-tasks'], (old) => {
        if (!old) return old

        // Find the task being moved
        const movingTask = old.find((t) => t.id === id)
        if (!movingTask) return old

        // Remove it from the current position
        const withoutMoving = old.filter((t) => t.id !== id)

        // Update its status
        const updatedTask = {
          ...movingTask,
          status,
        }

        // Group tasks by status
        const tasksByStatus: Record<string, TaskResponse[]> = {
          TODO: [],
          IN_PROGRESS: [],
          DONE: [],
        }

        // Organize existing tasks by status
        withoutMoving.forEach((task) => {
          tasksByStatus[task.status].push(task)
        })

        // Insert the moved task at the new position in the target status
        tasksByStatus[status].splice(position, 0, updatedTask)

        // Reconstruct the array in the correct order (status order, then position within status)
        const statusOrder = TASK_STATUSES
        const result: TaskResponse[] = []
        statusOrder.forEach((statusKey) => {
          result.push(...tasksByStatus[statusKey])
        })

        return result
      })

      // Return context with the previous value for rollback
      return { previousTasks }
    },
    // If the mutation fails, rollback to the previous value
    onError: (_err, _variables, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(['real-estate-tasks'], context.previousTasks)
      }
    },
    // On success, invalidate to refetch and ensure consistency
    onSuccess: () => {
      // Invalidate in the background to get the server's authoritative order
      queryClient.invalidateQueries({ queryKey: ['real-estate-tasks'] })
    },
  })
}
